import type Konva from 'konva';
import { useRef, useState } from 'react';
import { Circle, Group, Line, Rect, Text } from 'react-konva';
import { useActiveClip } from '~/features/clips/selectors';
import { snapPoint } from '~/lib/snap';
import { useStore } from '~/store';
import { setStageCursor } from './cursors';

const SNAP_RADIUS_STAGE_PX = 8;
const MARKER_COLOR = '#facc15';
const MARKER_HALO = 'rgba(15, 23, 42, 0.85)';
/** Center reticle arm length in screen px — independent of zoom. */
const RETICLE_ARM = 8;
/** Gap around the center dot so the crosshair doesn't pierce the dot. */
const RETICLE_GAP = 2;
const RETICLE_DOT = 2;

type Point = { x: number; y: number };

/** Snap candidates from frame 0's cellRect: corners, center, bottom-center. */
function cellSnapCandidates(cell: { x: number; y: number; w: number; h: number }): Point[] {
  const { x, y, w, h } = cell;
  return [
    { x, y },
    { x: x + w, y },
    { x, y: y + h },
    { x: x + w, y: y + h },
    { x: x + w / 2, y: y + h / 2 },
    { x: x + w / 2, y: y + h },
  ];
}

type MarkerProps = {
  x: number;
  y: number;
  radius: number;
  stageScale: number;
  dashed?: boolean;
};

function ReferenceMarker({ x, y, radius, stageScale, dashed = false }: MarkerProps) {
  const dash = dashed ? [6, 4] : undefined;
  // Reticle is rendered in source-image coords inside a scaled Stage, so divide
  // screen-px lengths by stageScale to keep it visually constant at any zoom.
  const arm = RETICLE_ARM / stageScale;
  const gap = RETICLE_GAP / stageScale;
  const dot = RETICLE_DOT / stageScale;
  return (
    <Group listening={false}>
      {/* Radius ring: solid thin line, dark halo + bright color, fixed screen-px strokes. */}
      <Circle
        x={x}
        y={y}
        radius={radius}
        stroke={MARKER_HALO}
        strokeWidth={3}
        strokeScaleEnabled={false}
        {...(dash ? { dash } : {})}
      />
      <Circle
        x={x}
        y={y}
        radius={radius}
        stroke={MARKER_COLOR}
        strokeWidth={1.5}
        strokeScaleEnabled={false}
        {...(dash ? { dash } : {})}
      />
      {/* Center reticle: crosshair + dot at fixed screen size — always crisp. */}
      <Line
        points={[x - arm, y, x - gap, y]}
        stroke={MARKER_HALO}
        strokeWidth={3}
        strokeScaleEnabled={false}
      />
      <Line
        points={[x + gap, y, x + arm, y]}
        stroke={MARKER_HALO}
        strokeWidth={3}
        strokeScaleEnabled={false}
      />
      <Line
        points={[x, y - arm, x, y - gap]}
        stroke={MARKER_HALO}
        strokeWidth={3}
        strokeScaleEnabled={false}
      />
      <Line
        points={[x, y + gap, x, y + arm]}
        stroke={MARKER_HALO}
        strokeWidth={3}
        strokeScaleEnabled={false}
      />
      <Line
        points={[x - arm, y, x - gap, y]}
        stroke={MARKER_COLOR}
        strokeWidth={1.5}
        strokeScaleEnabled={false}
      />
      <Line
        points={[x + gap, y, x + arm, y]}
        stroke={MARKER_COLOR}
        strokeWidth={1.5}
        strokeScaleEnabled={false}
      />
      <Line
        points={[x, y - arm, x, y - gap]}
        stroke={MARKER_COLOR}
        strokeWidth={1.5}
        strokeScaleEnabled={false}
      />
      <Line
        points={[x, y + gap, x, y + arm]}
        stroke={MARKER_COLOR}
        strokeWidth={1.5}
        strokeScaleEnabled={false}
      />
      <Circle x={x} y={y} radius={dot} fill={MARKER_COLOR} stroke={MARKER_HALO} strokeWidth={1} />
    </Group>
  );
}

/** Convert a Konva mouse event to source-image coordinates. */
function toSrc(e: Konva.KonvaEventObject<MouseEvent>): Point {
  const layer = e.target.getLayer();
  if (layer) {
    const rel = layer.getRelativePointerPosition();
    if (rel) return rel;
  }
  const stage = e.target.getStage();
  if (!stage) return { x: 0, y: 0 };
  const pos = stage.getPointerPosition();
  if (!pos) return { x: 0, y: 0 };
  return { x: pos.x, y: pos.y };
}

/**
 * Overlay for reference-point alignment mode.
 *
 * When `patch` is already defined: renders a yellow dashed ring + center dot.
 * When `patch` is null: renders a transparent fullsize capture rect that
 * accepts a click-drag gesture to define center + radius, then commits via
 * `setClipPatch`.  Center is snapped to frame[0]'s cellRect anchor points.
 *
 * stageScale is passed in from EditorStage (already synced to editor.stageScale).
 */
type ReferencePointOverlayProps = {
  stageScale: number;
};

export function ReferencePointOverlay({ stageScale }: ReferencePointOverlayProps) {
  const activeClip = useActiveClip();
  const setClipPatch = useStore((s) => s.setClipPatch);
  const dims = useStore((s) => s.source.dimensions);
  // For snapping: use frame[0]'s cellRect.  In pin mode this comes from blobs;
  // in uniform-grid mode blobs is null, so snapping is skipped gracefully.
  const frame0CellRect = useStore((s) => {
    const blobs = s.pin.blobs;
    if (blobs && blobs.length > 0) {
      const b = s.pin.overrides[0]?.bbox ?? blobs[0]?.bbox;
      return b ?? null;
    }
    return null;
  });

  const [dragCenter, setDragCenter] = useState<Point | null>(null);
  const [dragRadius, setDragRadius] = useState(0);
  const capturingRef = useRef(false);

  if (!activeClip) return null;
  if (activeClip.alignmentMode !== 'reference-point') return null;
  if (!dims) return null;

  const patch = activeClip.patch;

  if (patch) {
    return (
      <ReferenceMarker
        x={patch.x}
        y={patch.y}
        radius={patch.radius}
        stageScale={stageScale}
        dashed
      />
    );
  }

  // No patch yet — capture rect + live preview circles as siblings in a Group.
  return (
    <Group>
      <Text
        x={8}
        y={8}
        text="Click-drag on a feature in the first frame to define the match template."
        fontSize={12 / stageScale}
        fill="rgba(250, 204, 21, 0.95)"
        listening={false}
        width={420 / stageScale}
        wrap="word"
      />
      <Rect
        x={0}
        y={0}
        width={dims.w}
        height={dims.h}
        fill="transparent"
        onMouseEnter={(e) => setStageCursor(e, 'crosshair')}
        onMouseLeave={(e) => {
          if (capturingRef.current) return;
          setStageCursor(e, '');
        }}
        onMouseDown={(e) => {
          if (e.evt.button !== 0) return;
          const raw = toSrc(e);
          const snapped = frame0CellRect
            ? snapPoint(raw, cellSnapCandidates(frame0CellRect), SNAP_RADIUS_STAGE_PX, stageScale)
            : raw;
          capturingRef.current = true;
          setStageCursor(e, 'grabbing');
          setDragCenter(snapped);
          setDragRadius(0);
        }}
        onMouseMove={(e) => {
          if (!capturingRef.current || !dragCenter) return;
          const pos = toSrc(e);
          const r = Math.hypot(pos.x - dragCenter.x, pos.y - dragCenter.y);
          setDragRadius(r);
        }}
        onMouseUp={(e) => {
          if (!capturingRef.current || !dragCenter) return;
          capturingRef.current = false;
          setStageCursor(e, '');
          const pos = toSrc(e);
          const r = Math.max(2, Math.hypot(pos.x - dragCenter.x, pos.y - dragCenter.y));
          setClipPatch(activeClip.id, {
            frameIndex: 0,
            x: Math.round(dragCenter.x),
            y: Math.round(dragCenter.y),
            radius: Math.round(r),
          });
          setDragCenter(null);
          setDragRadius(0);
        }}
      />
      {dragCenter && dragRadius > 0 && (
        <ReferenceMarker
          x={dragCenter.x}
          y={dragCenter.y}
          radius={dragRadius}
          stageScale={stageScale}
          dashed
        />
      )}
    </Group>
  );
}
